import * as React from 'react';
import { useEffect, useState } from 'react';
import { Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { attractionsService, type AttractionBooking } from '../../services/attractions/attractionsService.ts';
import { ViatorLink } from '../../pages/attractions/AttractionsBookingsPage.tsx';
import { fmtAgo, fmtDateTime, fmtPlanDate } from '../../pages/attractions/attractionsShared.tsx';

/** More than any one customer plausibly marks; the full list is one click away. */
const LIMIT = 50;

const headCellSx = {
    paddingLeft: 'var(--space-24)',
    py: 'var(--space-16)',
    fontSize: 'var(--text-xs)',
    fontWeight: 700,
};

const bodyCellSx = {
    py: 'var(--space-16)',
    paddingLeft: 'var(--space-24)',
    fontSize: 'var(--text-xs)',
};

/**
 * "Attractions booked" on a customer's page: the attractions this customer
 * said they booked on Viator, every date, latest first. Admins only, like
 * Attractions › Bookings, which uses the same endpoint.
 */
const CustomerAttractionBookingsPanel: React.FC<{ customerId: number }> = ({ customerId }) => {
    const [rows, setRows] = useState<AttractionBooking[] | null>(null);
    const [total, setTotal] = useState(0);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        attractionsService.bookings({ when: 'all', customerId }, 0, LIMIT)
            .then(page => { if (!cancelled) { setRows(page.rows); setTotal(page.total); } })
            .catch(e => { Sentry.captureException(e); if (!cancelled) setFailed(true); });
        return () => { cancelled = true; };
    }, [customerId]);

    return (
        <Box data-customer-attractions="1" sx={{ mt: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 'var(--space-16)' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 'var(--text-xxl)' }}>Attractions booked</Typography>
                {rows && total > 0 && (
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                        {total} marked by the customer · <Box component={Link} to="/attractions/bookings" sx={{ color: 'var(--color-primary)' }}>All bookings</Box>
                    </Typography>
                )}
            </Box>
            <Box sx={{ border: '1px solid var(--color-secondary)', borderRadius: 'var(--radius-6)', overflow: 'hidden', width: '100%' }}>
                {!rows && !failed && <Box sx={{ py: 3, pl: 'var(--space-24)' }}><CircularProgress size={20} /></Box>}
                {failed && (
                    <Typography sx={{ p: 'var(--space-24)', fontSize: 'var(--text-xs)', color: 'var(--color-error)' }}>
                        Could not load this customer’s attractions.
                    </Typography>
                )}
                {rows && rows.length === 0 && (
                    <Typography sx={{ p: 'var(--space-24)', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                        No attractions marked as booked. They appear here when the customer answers “Yes” to “Did you book it?” in the app.
                    </Typography>
                )}
                {rows && rows.length > 0 && (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: 640 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headCellSx}>Date &amp; time</TableCell>
                                    <TableCell sx={headCellSx}>Attraction</TableCell>
                                    <TableCell sx={headCellSx}>Marked booked</TableCell>
                                    <TableCell sx={headCellSx}>Viator</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(b => (
                                    <TableRow key={b.id}>
                                        <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap', fontWeight: 700 }}>{fmtPlanDate(b.planDate, b.startTime)}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <div>{b.title}</div>
                                            <Box sx={{ color: 'var(--color-text-secondary)' }}>{b.cityName ?? ''}</Box>
                                        </TableCell>
                                        <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }} title={fmtDateTime(b.markedAt)}>{fmtAgo(b.markedAt)}</TableCell>
                                        <TableCell sx={bodyCellSx}><ViatorLink b={b} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Box>
            <Typography sx={{ mt: 1, fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                The customer’s own word after booking on Viator. Confirmed bookings are in the Viator partner dashboard.
            </Typography>
        </Box>
    );
};

export default CustomerAttractionBookingsPanel;
